/**
 * @file Encart d'ouverture d'un nouveau restaurant, monté au-dessus du calendrier à la place des prochaines disponibilités.
 * @module packages.common.components.table-reservation.components.OpeningBanner
 *
 * Tant que le réglage d'avant-ouverture a un effet (exposé par la config publique),
 * le client voit une annonce plutôt qu'un mois vide : la date d'ouverture, puis
 * soit l'invitation à réserver, soit la période de liste d'attente de lancement et
 * le premier jour réservable. Titre et détail personnalisés, quand ils sont
 * renseignés, remplacent chacun leur texte automatique (ex. date d'ouverture encore
 * inconnue) en gardant leur mise en forme. Le réglage
 * est posé par le super administrateur ; le serveur décide seul de ce qui est
 * réservable, l'encart ne fait qu'annoncer.
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
// icons
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {EYEBROW_SX} from './NextAvailabilityBanner';
import {useLocalizedText} from 'packages/common/utils/multiLanguageHelper';
import {buildOpeningDefaultTexts} from 'packages/common/utils/restaurant/preOpening';

const CONTENT_FADE_MS = 300;
const EYEBROW_ICON_SIZE = 18;

/**
 * Encart d'ouverture d'un nouveau restaurant.
 *
 * @param {Object} props
 * @param {Object} props.preOpening - Réglage actif `{openingDate, waitlistUntil, title, detail}` (jours 'yyyy-MM-dd')
 * @param {string} [props.restaurantName] - Nom de l'établissement
 * @param {Object} [props.dateFnsLocale] - Locale date-fns courante
 * @param {Function} props.t - Fonction de traduction générique
 * @return {JSX.Element}
 */
export default function OpeningBanner({preOpening, restaurantName, dateFnsLocale, t}) {
  const formatOptions = useMemo(() => (dateFnsLocale ? {locale: dateFnsLocale} : undefined), [dateFnsLocale]);
  const getLocalizedText = useLocalizedText();

  const labels = useMemo(() => {
    const defaults = buildOpeningDefaultTexts({preOpening, restaurantName, t, formatOptions, today: new Date()});
    return {
      title: getLocalizedText(preOpening.title) || defaults.title,
      detail: getLocalizedText(preOpening.detail) || defaults.detail,
    };
  }, [preOpening, restaurantName, t, formatOptions, getLocalizedText]);

  return (
    <Fade in appear timeout={CONTENT_FADE_MS}>
      <Box
        role="status"
        sx={{
          mb: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <Box sx={{...EYEBROW_SX, mb: 0.75}}>
          <StorefrontOutlinedIcon sx={{fontSize: EYEBROW_ICON_SIZE}} aria-hidden="true" />
          {t('widgets.reservation.preOpening.eyebrow')}
        </Box>
        <Typography variant="body2" sx={{fontWeight: 600, lineHeight: 1.4, color: 'text.primary', whiteSpace: 'pre-line'}}>
          {labels.title}
        </Typography>
        <Typography variant="body2" sx={{mt: 0.5, lineHeight: 1.5, color: 'text.secondary', whiteSpace: 'pre-line'}}>
          {labels.detail}
        </Typography>
      </Box>
    </Fade>
  );
}

OpeningBanner.propTypes = {
  preOpening: PropTypes.shape({
    openingDate: PropTypes.string.isRequired,
    waitlistUntil: PropTypes.string,
    title: PropTypes.object,
    detail: PropTypes.object,
  }).isRequired,
  restaurantName: PropTypes.string,
  dateFnsLocale: PropTypes.object,
  t: PropTypes.func.isRequired,
};
